import * as RadixToast from '@radix-ui/react-toast';
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { cn } from '../utils/cn';

type ToastTone = 'info' | 'success' | 'danger';
interface ToastAction {
  label: string;
  onClick: () => void;
}
interface ToastItem {
  id: string;
  title: string;
  description?: string;
  tone: ToastTone;
  action?: ToastAction;
}

interface ToastApi {
  push(input: { title: string; description?: string; tone?: ToastTone; action?: ToastAction }): void;
}

const ToastCtx = createContext<ToastApi | null>(null);

export function useToast(): ToastApi {
  const ctx = useContext(ToastCtx);
  if (!ctx) throw new Error('ToastProvider not mounted');
  return ctx;
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);

  const push = useCallback<ToastApi['push']>((input) => {
    const id = Math.random().toString(36).slice(2);
    setItems((xs) => [
      ...xs,
      {
        id,
        title: input.title,
        description: input.description,
        tone: input.tone ?? 'info',
        action: input.action,
      },
    ]);
  }, []);

  const api = useMemo(() => ({ push }), [push]);

  return (
    <ToastCtx.Provider value={api}>
      <RadixToast.Provider swipeDirection="down" duration={4500}>
        {children}
        {items.map((t) => (
          <RadixToast.Root
            key={t.id}
            onOpenChange={(open) => {
              if (!open) setItems((xs) => xs.filter((x) => x.id !== t.id));
            }}
            className={cn(
              'border bg-surface text-ink px-4 py-3 rounded-md w-[min(360px,90vw)] elev-pop',
              t.tone === 'success' && 'border-olive bg-olive-tint',
              t.tone === 'danger' && 'border-clay bg-clay-tint text-clay-deep',
              t.tone === 'info' && 'border-rule-strong',
            )}
          >
            <div className="flex items-center justify-between gap-3">
              <div>
                <RadixToast.Title className="font-semibold text-[15px]">{t.title}</RadixToast.Title>
                {t.description ? (
                  <RadixToast.Description className="text-[13px] text-graphite mt-0.5">
                    {t.description}
                  </RadixToast.Description>
                ) : null}
              </div>
              {t.action ? (
                <RadixToast.Action altText={t.action.label} asChild>
                  <button
                    type="button"
                    onClick={t.action.onClick}
                    className="shrink-0 text-petrol font-semibold text-[14px] underline-offset-2 hover:underline"
                  >
                    {t.action.label}
                  </button>
                </RadixToast.Action>
              ) : null}
            </div>
          </RadixToast.Root>
        ))}
        <RadixToast.Viewport className="fixed bottom-6 end-6 z-50 flex flex-col gap-2 outline-none" />
      </RadixToast.Provider>
    </ToastCtx.Provider>
  );
}
